import { useEffect, useContext } from "react"
import * as Yup from "yup"
import GenericAssetForm from "./common/GenericAssetForm"
import { useFormikContext } from "formik"

// Property initial values
export const propertyInitialValues = {
  propertyType: "",
  propertyAddress: "",
  ownershipType: "",
  ownershipPercentage: "",
  "rentalIncome.amount": "",
  "rentalIncome.frequency": "",
}

// Property validation schema
export const propertyValidationSchema = Yup.object().shape({
  name: Yup.string().required("Property Name is required")
    .min(3, "Min length: 3 characters")
    .max(100, "Max length: 100 characters")
    .matches(
      /^(?! )[A-Za-z0-9\-_.&()]+( [A-Za-z0-9\-_.&()]+)*(?<! )$/,
      "Allowed: letters, numbers, -, _, &, ., (, ). No leading/trailing/blank spaces."
    ),
  purchaseDate: Yup.string().required("Purchase Date is required"),
  purchasePrice: Yup.number()
    .typeError("Must be a positive number")
    .required("Purchase price/amount is required")
    .positive("Must be a positive number > 0")
    .min(1, "Must be greater than 0"),
  propertyType: Yup.string().required("Property type is required"),
  propertyAddress: Yup.string().required("Property address is required"),
  ownershipType: Yup.string().required("Ownership type is required"),
  ownershipPercentage: Yup.number()
  .typeError("Must be a number")
  .required("Ownership Percentage is required")
  .positive("Must be greater than 0")
  .min(0.01, "Must be greater than 0")
  .max(100, "Ownership percentage cannot be more than 100"),
  rentalIncome: Yup.object().shape({
    amount: Yup.number()
      .typeError('Must be a number')
      .notRequired()
      .test('not-zero', 'Must be greater than 0', (value) => value === undefined || value === '' || value !== 0)
      .positive('Must be greater than 0')
      .min(0.01, 'Must be greater than 0'),
  }),
  "rentalIncome.frequency": Yup.string()
    .oneOf(['weekly','fortnightly','monthly','quarterly','halfyearly','yearly'])
    .notRequired(),
})

/**
 * Property Form - Field-only component for AddAssets.js
 * This renders only the property-specific fields without form wrapper
 * Used in ADD mode within AddAssets.js's existing Formik wrapper
 */
const PropertyForm = ({ values, setFieldValue, errors, touched, defaultCurrency }) => {
  const { setFieldTouched } = useFormikContext() || {}
  
  // Handle default currency setting
  useEffect(() => {
    if ((!values.purchaseCurrency || values.purchaseCurrency === "") && defaultCurrency) {
      setFieldValue("purchaseCurrency", defaultCurrency)
    }
  }, [values.purchaseCurrency, defaultCurrency, setFieldValue])

  // Initialize rentalIncome.frequency default value only if there's an amount
  useEffect(() => {
    if (values['rentalIncome.amount'] && !values['rentalIncome.frequency']) {
      setFieldValue("rentalIncome.frequency", "monthly")
    }
  }, [values, setFieldValue])

  return (
    <GenericAssetForm
      subCategory="property"
      values={values}
      setFieldValue={setFieldValue}
      setFieldTouched={setFieldTouched}
      errors={errors}
      touched={touched}
      defaultCurrency={defaultCurrency}
      readOnly={false}
    />
  )
}

export default PropertyForm
